import './won-offer-filters.scss';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoadFilterSimple from '../../../../../common/components/load-filters/load-filter-simple/load-filter-simple';
import IWonFiltersModel from '../../../../../common/interfaces/IFiltersModel';
import LoadFilterAdvanced from '../../../../../common/components/load-filters/load-filter-advanced/load-filter-advanced';
import LoadFilterTags from '../../../../../common/components/load-filters/load-filter-tags/load-filter-tags';
import IWonOfferFiltersProps from './IWonOfferFiltersProps';
import { getSelectOptionsEnumWonOfferStatus } from '../../utils/won-offer-utils';
import {
    setChannelFilterProps,
    setDateFilterProps,
    setMaterialFilterProps,
    setSourceLocationFilterProps,
    setDestinationLocationFilterProps,
} from '../../../../../common/utils/load-offers-utils';
import RoundmanagerApi from '../../../../../common/services/roundmanager-api';
import ILoadMaterialResponse from '../../../../../common/interfaces/loads/ILoadMaterialResponse';
import { useToast } from '../../../../../app/contexts/toast/toast.context';
import ISelectInputModel from '../../../../../common/components/load-filters/load-filter-advanced/components/location-filter/interfaces/ISelectInputModel';
import ILoadsLocationModel from '../../../../../common/interfaces/loads/ILoadsLocationModel';
import LoadsFilterLocationType from '../../../../../common/enums/loads/loads-filter-tags-location-type.enum';
import ILoadsLocationResponse from '../../../../../common/interfaces/loads/ILoadsLocationResponse';
import { useOffersWon } from '../../../../../app/contexts/offers-won/offers-won.context';
import { getLoadChannelOptions } from '../../../../../common/enums/loads/load-channel-types.enum';
import { getLabelDescription } from '../../../../../utils/string-utils';

function WonOfferFilters({ filters }: Readonly<IWonOfferFiltersProps>) {
    const { t } = useTranslation();
    const { setToast } = useToast();
    const { fetchFirstPage } = useOffersWon();

    const [openAdvancedFilters, setOpenAdvancedFilters] = useState(false);
    const [advancedFilter, setAdvancedFilter] = useState<
        Partial<IWonFiltersModel>
    >(filters.current);

    const [materialOptions, setMaterialOptions] = useState<ISelectInputModel[]>(
        [] as ISelectInputModel[],
    );

    const [sourceUFOptions, setSourceUFOptions] = useState<ISelectInputModel[]>(
        [] as ISelectInputModel[],
    );
    const [sourceDescriptionOptions, setSourceDescriptionOptions] = useState<
        ISelectInputModel[]
    >([] as ISelectInputModel[]);

    const [destinationUFOptions, setDestinationUFOptions] = useState<
        ISelectInputModel[]
    >([] as ISelectInputModel[]);
    const [destinationDescriptionOptions, setDestinationDescriptionOptions] =
        useState<ISelectInputModel[]>([] as ISelectInputModel[]);

    const advancedFiltersRef = useRef<IWonFiltersModel>({} as IWonFiltersModel);

    const onApplyFilters = useCallback(
        (filtersModel: IWonFiltersModel) => {
            filters.current = filtersModel;
            fetchFirstPage(filters.current);
        },
        [filters, fetchFirstPage],
    );

    const setAdvancedFilters = useCallback((filtersModel: IWonFiltersModel) => {
        advancedFiltersRef.current = filtersModel;
        setAdvancedFilter(filtersModel);
    }, []);

    const handleMaterialSearch = useCallback(
        async (value?: string) => {
            return RoundmanagerApi.Offers.GetMaterials(value).then(
                ({ response, error }) => {
                    if (error !== undefined) {
                        setToast('negative', t('Error'), error.message);
                        return [];
                    }

                    return response!.data;
                },
            );
        },
        [setToast, t],
    );

    const handleMaterialSearchResult = useCallback(
        (data: ILoadMaterialResponse[]) => {
            const selectOptions = [] as ISelectInputModel[];

            const options = new Set(data.map(mat => mat));

            options.forEach(mat => {
                selectOptions.push({
                    key: mat.id.toString(),
                    label: mat.name,
                    value: mat.id,
                });
            });

            setMaterialOptions(selectOptions);
        },
        [setMaterialOptions],
    );

    const fetchMaterialsFilterValues = useCallback(async () => {
        const response = await handleMaterialSearch();
        handleMaterialSearchResult(response);
    }, [handleMaterialSearch, handleMaterialSearchResult]);

    const getOffersLocations = useCallback(
        async (filter: ILoadsLocationModel) => {
            return RoundmanagerApi.Offers.GetLocations(filter);
        },
        [],
    );

    const getUFResultOptions = useCallback((states: string[]) => {
        const options = [] as ISelectInputModel[];

        const resultSet = new Set(states);
        resultSet.forEach((item, idx) => {
            options.push({
                key: idx.toString(),
                label: item,
                value: item,
            });
        });

        return options;
    }, []);

    const handleSourceDescriptionSearch = useCallback(
        (value?: string) => {
            return getOffersLocations({
                locationType: LoadsFilterLocationType.Source,
                search: value,
            } as ILoadsLocationModel).then(({ response, error }) => {
                if (error !== undefined) {
                    setToast('negative', t('Error'), error.message);
                    return [];
                }

                return response!.data;
            });
        },
        [getOffersLocations, setToast, t],
    );

    const handleSourceDescriptionSearchResult = useCallback(
        (data: ILoadsLocationResponse[]) => {
            const locationsStates: string[] = [];
            const sourceDescriptions = [] as ISelectInputModel[];

            data.forEach((sourceDesc, index) => {
                sourceDescriptions.push({
                    key: index?.toString(),
                    label: getLabelDescription(
                        sourceDesc.description,
                        sourceDesc.code,
                    ),
                    value: sourceDesc.code,
                });

                locationsStates.push(sourceDesc.federativeUnit);
            });

            setSourceDescriptionOptions(sourceDescriptions);
            setSourceUFOptions(getUFResultOptions(locationsStates));
        },
        [setSourceDescriptionOptions, getUFResultOptions],
    );

    const handleDestinationDescriptionSearch = useCallback(
        (value?: string) => {
            return getOffersLocations({
                locationType: LoadsFilterLocationType.Destination,
                search: value,
            } as ILoadsLocationModel).then(({ response, error }) => {
                if (error !== undefined) {
                    setToast('negative', t('Error'), error.message);
                    return [];
                }

                return response!.data;
            });
        },
        [getOffersLocations, setToast, t],
    );

    const handleDestinationDescriptionSearchResult = useCallback(
        (data: ILoadsLocationResponse[]) => {
            const locationsStates: string[] = [];
            const destOptions: ISelectInputModel[] = [];

            data.forEach((item, index) => {
                destOptions.push({
                    key: index.toString(),
                    label: getLabelDescription(item.description, item.code),
                    value: item.code,
                });

                locationsStates.push(item.federativeUnit);
            });

            setDestinationDescriptionOptions(destOptions);
            setDestinationUFOptions(getUFResultOptions(locationsStates));
        },
        [setDestinationDescriptionOptions, getUFResultOptions],
    );

    const fetchSourceLocationsFilterValues = useCallback(async () => {
        const response = await handleSourceDescriptionSearch();
        handleSourceDescriptionSearchResult(response);
    }, [handleSourceDescriptionSearch, handleSourceDescriptionSearchResult]);

    const fetchDestinationLocationsFilterValues = useCallback(async () => {
        const response = await handleDestinationDescriptionSearch();
        handleDestinationDescriptionSearchResult(response);
    }, [
        handleDestinationDescriptionSearch,
        handleDestinationDescriptionSearchResult,
    ]);

    const setFilters = useCallback(
        (filtersModel: IWonFiltersModel) => {
            onApplyFilters(filtersModel);
        },
        [onApplyFilters],
    );

    const openAdvancedFiltersDrawer = useCallback(() => {
        setOpenAdvancedFilters(true);
        setAdvancedFilters(filters.current);
    }, [filters, setAdvancedFilters]);

    const closeAdvancedFiltersDrawer = useCallback(() => {
        advancedFiltersRef.current = {};
        setOpenAdvancedFilters(false);
        setAdvancedFilter(filters.current);
    }, [filters]);

    const onApplyAdvancedFiltersFilters = useCallback(() => {
        filters.current = advancedFiltersRef.current;
        closeAdvancedFiltersDrawer();
        fetchFirstPage(filters.current);
    }, [filters, closeAdvancedFiltersDrawer, fetchFirstPage]);

    const cleanCurrentFilters = useCallback(() => {
        onApplyFilters({});
        closeAdvancedFiltersDrawer();
    }, [closeAdvancedFiltersDrawer, onApplyFilters]);

    const getWonOfferStatusOptions = () => {
        const status = getSelectOptionsEnumWonOfferStatus();

        status.forEach(s => {
            s.label = t(s.label);
        });

        return status;
    };

    const dateFilterProps = setDateFilterProps(
        advancedFilter,
        setAdvancedFilters,
        new Date(),
    );

    const channelFilterProps = setChannelFilterProps(
        advancedFilter,
        setAdvancedFilters,
        getLoadChannelOptions,
    );

    const materialFilterProps = setMaterialFilterProps(
        advancedFilter,
        setAdvancedFilters,
        handleMaterialSearch,
        handleMaterialSearchResult,
        materialOptions,
    );

    const sourceLocationFilterProps = setSourceLocationFilterProps(
        advancedFilter,
        sourceUFOptions,
        setAdvancedFilters,
        sourceDescriptionOptions,
        handleSourceDescriptionSearch,
        handleSourceDescriptionSearchResult,
    );

    const destinationLocationFilterProps = setDestinationLocationFilterProps(
        advancedFilter,
        destinationUFOptions,
        setAdvancedFilters,
        destinationDescriptionOptions,
        handleDestinationDescriptionSearch,
        handleDestinationDescriptionSearchResult,
    );

    useEffect(() => {
        fetchMaterialsFilterValues();
        fetchSourceLocationsFilterValues();
        fetchDestinationLocationsFilterValues();
    }, [
        fetchMaterialsFilterValues,
        fetchSourceLocationsFilterValues,
        fetchDestinationLocationsFilterValues,
    ]);

    return (
        <div className="offers-grid-filters">
            <div className="offers-filters-container">
                <LoadFilterSimple
                    filters={filters}
                    setOpenAdvancedFilter={openAdvancedFiltersDrawer}
                    setFilters={setFilters}
                    getLoadStatusOptions={getWonOfferStatusOptions}
                    getLoadChannelOptions={getLoadChannelOptions}
                />

                <LoadFilterAdvanced
                    open={openAdvancedFilters}
                    onClose={closeAdvancedFiltersDrawer}
                    onApply={onApplyAdvancedFiltersFilters}
                    onClear={cleanCurrentFilters}
                    dateFilterProps={dateFilterProps()}
                    channelFilterProps={channelFilterProps()}
                    materialFilterProps={materialFilterProps()}
                    sourceLocationFilterProps={sourceLocationFilterProps()}
                    destinationLocationFilterProps={destinationLocationFilterProps()}
                />
            </div>

            <LoadFilterTags
                filters={filters}
                selectedCards={[]}
                materialOptions={materialOptions}
                onSelectAllCards={() => {}}
                onCleanCardSelection={() => {}}
                cleanCurrentFilters={cleanCurrentFilters}
                onApplyFilters={onApplyFilters}
            />
        </div>
    );
}

export default WonOfferFilters;
